import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../../App.styles';
import { Typography } from '../../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { storeOverviewTranslations } from '../../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../../utils';
import { HeaderTooltip } from '../../../headerTooltip/HeaderTooltip';
import { getIndexStatus } from '../../sales.util';
import { POTENTIAL_SECTION_MIN_WIDTH, PotentialItem } from '../util';
import { StyledMetricContainer, StyledTypography } from './PotentialSection.styles';

const MetricCard = ({ isArea, value }: { isArea: boolean; value: number }) => {
  const { t } = useTranslation();
  const status = getIndexStatus(value);
  const { headerTooltips: headerTooltipsTranslations } = storeOverviewTranslations;
  return (
    <StyledMetricContainer
      status={status}
      padding={[1, 2]}
      gap={[1]}
      borderRadius={[1]}
      data-testid="potential-metric-card"
    >
      <HeaderTooltip
        headerText={isArea ? 'Area Takt' : 'vs LY'}
        isSecondaryText
        headerTextType="caption"
        tooltipText={
          isArea
            ? t(headerTooltipsTranslations.areaTakt.key, headerTooltipsTranslations.areaTakt.defaultValue)
            : t(headerTooltipsTranslations.vsLY.key, headerTooltipsTranslations.vsLY.defaultValue)
        }
      />
      <StyledTypography type="caption" status={status}>
        {value}
      </StyledTypography>
    </StyledMetricContainer>
  );
};

type PotentialSectionProps = {
  title: string;
  subTitle: string;
  data: PotentialItem[];
  showDescription: boolean;
  fullWidth: boolean;
};

export const PotentialSection = ({ title, subTitle, data, showDescription, fullWidth }: PotentialSectionProps) => {
  const { t } = useTranslation();
  const { headerTooltips: headerTooltipsTranslations } = storeOverviewTranslations;
  return (
    <ColumnFlexContainer
      flex={fullWidth ? 1 : undefined}
      height="auto"
      minWidth={[POTENTIAL_SECTION_MIN_WIDTH]}
      data-testid="potential-section"
    >
      <Typography type="button">{title}</Typography>
      <SecondaryTextTypography margin={[0, 0, 2]}>{subTitle}</SecondaryTextTypography>
      <ColumnFlexContainer gap={[2]}>
        {data.map((item) => (
          <RowFlexContainer
            key={item.id}
            backgroundColor="backgroundSelectedDark"
            padding={[2]}
            gap={[1]}
            borderRadius={[1]}
          >
            <ColumnFlexContainer gap={[1]} flex={1}>
              <Typography>{showDescription ? `${item.id} - ${item.description}` : item.id}</Typography>
              <RowFlexContainer gap={[1]}>
                <MetricCard isArea value={item.idxArea} />
                <MetricCard isArea={false} value={item.vsLY} />
              </RowFlexContainer>
            </ColumnFlexContainer>
            <ColumnFlexContainer alignItems="end">
              <Typography type="body4">{formatAmountWithSeparator(item.potentialGain, null, 0)}</Typography>
              <HeaderTooltip
                headerText="Gain"
                isSecondaryText
                headerTextType="caption"
                tooltipText={t(
                  headerTooltipsTranslations.potentialGain.key,
                  headerTooltipsTranslations.potentialGain.defaultValue,
                )}
              />
            </ColumnFlexContainer>
          </RowFlexContainer>
        ))}
      </ColumnFlexContainer>
    </ColumnFlexContainer>
  );
};
